/**
 * SettingsSearch — ⌘K command palette over every setting in the Console.
 *
 * Index is built from the areas registry (title + description + keywords per
 * section), grouped by area. Selecting a result jumps to the area and scrolls
 * to the section anchor — replacing the old page's hash-scroll machinery as
 * the way to reach a buried setting.
 */
import { useEffect, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { SETTINGS_AREAS, type SettingsAreaId } from '../../areas';
import { ScopeBadge } from './ScopeBadge';

interface SettingsSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJump: (area: SettingsAreaId, sectionId: string) => void;
  isAdmin: boolean;
}

export function SettingsSearch({ open, onOpenChange, onJump, isAdmin }: SettingsSearchProps) {
  return (
    // Composed inline (not shadcn CommandDialog) so the dialog carries an
    // sr-only DialogTitle — Radix errors on titleless DialogContent.
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <DialogTitle className="sr-only">Search settings</DialogTitle>
        <DialogDescription className="sr-only">
          Type to filter settings, then press Enter to jump to one.
        </DialogDescription>
        <Command
          // Substring match (word-boundary friendly) instead of cmdk's loose
          // fuzzy subsequence scoring — "redo" must NOT rank "shipping
          // provider" above "Redo reasons".
          filter={(value, search) =>
            value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0
          }
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5"
        >
      <CommandInput placeholder="Jump to any setting…" />
      <CommandList>
        <CommandEmpty>No setting matches.</CommandEmpty>
        {SETTINGS_AREAS.map((area) => {
          const sections = area.sections.filter((s) => isAdmin || !s.adminOnly);
          if (sections.length === 0) return null;
          const Icon = area.icon;
          return (
            <CommandGroup key={area.id} heading={area.title}>
              {sections.map((section) => (
                <CommandItem
                  key={`${area.id}:${section.id}`}
                  value={`${area.title} ${section.title} ${section.description} ${(section.keywords ?? []).join(' ')}`}
                  onSelect={() => {
                    onOpenChange(false);
                    onJump(area.id, section.id);
                  }}
                >
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{section.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{section.description}</p>
                  </div>
                  {section.scope ? <ScopeBadge scope={section.scope} className="ml-2" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** Global ⌘K / Ctrl+K binding for the settings palette. */
export function useSettingsSearchHotkey(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return [open, setOpen];
}
